import { inject, Injectable } from '@angular/core';
import { AuthService } from '../services/Auth/auth-service';
import { Router, UrlTree } from '@angular/router';
import { ActivatedRouteSnapshot } from '@angular/router';
import { RouterStateSnapshot } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { TimesheetStatusService, TimesheetStatusResult } from '../services/timesheet-status/timesheet-status.service';
import { ToastrService } from 'ngx-toastr';
import { resolveLoginRedirectCandidate } from '../utils/legacy-workluge-url.util';

@Injectable({
  providedIn: 'root'
})
// export class Guard {
//   constructor(private authService: AuthService, private router: Router) { }

//   canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
//     return this.authService.isLoggedIn()
//       ? true
//       : this.router.createUrlTree(['/login-new']);
//   }
// }

// @Injectable({
//   providedIn: 'root'
// })
// export class UrlRestrictionGuard {
//   constructor(private authService: AuthService, private router: Router) { }

//   canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
//     const restrictedUrls = ['/people', '/settings'];
//     // const user = this.authService.getUser();

//     if (this.authService.isLoggedIn()) {
//       if (restrictedUrls.some(url => state.url.startsWith(url))) {
//         // if (user?.role === 'admin') {
//         //   return true;
//         // }
//         return this.router.createUrlTree(['/unauthorized']);
//       }
//       return true;
//     }

//     return this.router.createUrlTree(['/login-new'], { queryParams: { redirectURL: state.url } });
//   }
// }


@Injectable({
  providedIn: 'root'
})
export class Guard {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (this.authService.isLoggedIn()) {
      return true;
    }

    this.authService.storePostLoginRedirect(state.url);
    return this.router.createUrlTree(['/login']);
  }
}

@Injectable({
  providedIn: 'root'
})
export class UrlRestrictionGuard {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(
    route: ActivatedRouteSnapshot, 
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    
    const restrictedUrls = ['/people', '/settings'];
    
    // First check if user is authenticated
    if (!this.authService.isLoggedIn()) {
      this.authService.storePostLoginRedirect(state.url);
      return this.router.createUrlTree(['/login'], { 
        queryParams: { redirectURL: state.url } 
      });
    }

    // Check if the URL is restricted
    if (restrictedUrls.some(url => state.url.startsWith(url))) {
      const user = this.authService.getUser();
      
      // Check if user has admin role (you can customize this logic)
      if (user?.utype === 'Admin' || user?.accesslevel === 'admin') {
        return true;
      }
      return this.router.createUrlTree(['/unauthorized']);
    }
    return true;
  }
}

@Injectable({
  providedIn: 'root'
})
export class LoginGuard {
  private authService = inject(AuthService);
  private router = inject(Router);
  private timesheetStatusService = inject(TimesheetStatusService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    // Persist email deep-link before redirecting an already-authenticated user away from login.
    const redirectParam = resolveLoginRedirectCandidate({
      queryUrl: route.queryParamMap.get('url'),
      queryRedirectURL: route.queryParamMap.get('redirectURL'),
      queryRedirectUrl: route.queryParamMap.get('redirectUrl'),
      fragment: route.fragment,
      rawHash: typeof window !== 'undefined' ? window.location.hash : null,
    });
    if (redirectParam) {
      this.authService.storePostLoginRedirect(redirectParam);
    }

    if (!this.authService.isLoggedIn() && !this.timesheetStatusService.hasAuthSession()) {
      return true;
    }

    return this.timesheetStatusService.ensureStatus().pipe(
      map((status: TimesheetStatusResult) => {
        const fallback = status.success
          ? this.authService.getDefaultAuthenticatedRoute()
          : '/timesheets';
        return this.router.parseUrl(this.authService.getRedirectAfterLogin(fallback));
      }),
      catchError(() =>
        of(
          this.router.parseUrl(
            this.authService.getRedirectAfterLogin(this.authService.getDefaultAuthenticatedRoute())
          )
        )
      )
    );
  }
}

@Injectable({
  providedIn: 'root'
})
export class TimesheetSubmissionGuard {
  private authService = inject(AuthService);
  private router = inject(Router);
  private timesheetStatusService = inject(TimesheetStatusService);
  private toastr = inject(ToastrService);

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    return this.checkTimesheetAccess(state.url);
  }

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    return this.checkTimesheetAccess(state.url);
  }

  private checkTimesheetAccess(url: string): boolean | UrlTree | Observable<boolean | UrlTree> {
    if (!this.isAuthenticatedUser()) {
      return true;
    }

    if (this.timesheetStatusService.isTimesheetRoute(url)) {
      return this.timesheetStatusService.ensureStatus().pipe(map(() => true));
    }

    return this.timesheetStatusService.ensureStatus().pipe(
      map((status: TimesheetStatusResult) => {
        if (status.success) {
          return true;
        }

        const message = status.message || 'Please submit your timesheet.';
        // this.toastr.warning(message, '', { timeOut: 6000 });
        this.timesheetStatusService.openSubmissionWarning();

        return this.router.createUrlTree(['/timesheets']);
      }),
      catchError(() => of(true))
    );
  }

  private isAuthenticatedUser(): boolean {
    return this.authService.isLoggedIn() || this.timesheetStatusService.hasAuthSession();
  }
}